/**
 * Canonical consumer subscription entitlements.
 * Keep SQL helpers in sync (see consumer_entitlements_for_tier in migrations).
 */

/** Paid checkout tiers only — never offer `free` in Abo/Paywall maps. */
export const PAID_CONSUMER_TIERS = ["member", "circle_plus", "elite"] as const;
export type PaidConsumerTier = (typeof PAID_CONSUMER_TIERS)[number];

/** All consumer tiers including Freemium (`free`). Rank: free < member < circle_plus < elite. */
export const CONSUMER_TIERS = ["free", "member", "circle_plus", "elite"] as const;
export type ConsumerTier = (typeof CONSUMER_TIERS)[number];

export type AccessKind = "trialing" | "active" | "expired";

/** Store-managed billing that cannot be canceled server-side on account delete. */
export type StoreBillingDeleteWarningSource = "apple" | "google";

/**
 * Apple/Google IAP keep charging after Pulse account deletion until the user
 * cancels in store settings. Stripe is canceled in purgeUserAccount.
 */
export function resolveStoreBillingDeleteWarning(
  subscriptionSource: string | null | undefined,
  accessKind: AccessKind | null | undefined,
): StoreBillingDeleteWarningSource | null {
  if (accessKind !== "active" && accessKind !== "trialing") return null;
  if (subscriptionSource === "apple" || subscriptionSource === "google") {
    return subscriptionSource;
  }
  return null;
}

export type NotificationGranularity = "basic" | "extended";

export type ThemeUnlockMode = "platinum_only" | "all";

/**
 * Explicit Discovery staffelung delays after `events.created_at` (hours).
 * Source of truth for `getEventVisibilityForTier` — keep SQL RPC in sync.
 *
 * | Tier      | Delay |
 * |-----------|-------|
 * | elite     | 0h    |
 * | circle+   | 48h   |
 * | member    | 72h   |
 * | free      | 120h  |
 */
export const EVENT_VISIBILITY_DELAY_HOURS: Record<ConsumerTier, number> = {
  elite: 0,
  circle_plus: 48,
  member: 72,
  free: 120,
};

/** `null` on numeric limits means unlimited. */
export type ConsumerEntitlements = {
  maxSearchRadius: number | null;
  eventVisibilityHours: number | null;
  maxInterestedMarkers: number | null;
  maxSquads: number | null;
  maxMemoriesPhotos: number | null;
  recommendationCount: number;
  /**
   * Hours after `created_at` before a listing is visible to this tier.
   * Prefer this over the legacy `earlyAccessHours` relative-to-elite formula.
   */
  eventVisibilityDelayHours: number;
  /**
   * @deprecated Prefer `eventVisibilityDelayHours`. Kept for SQL/UI mirror:
   * historically `elite.earlyAccessHours - delay` with elite anchor 72.
   */
  earlyAccessHours: number;
  notificationGranularity: NotificationGranularity;
  prioritySupport: boolean;
  profileFlair: boolean;
  aiConciergeEnabled: boolean;
  /** Freemium: Circle tab / squad features gated. */
  circleEnabled: boolean;
  /** Freemium: Tickets tab / purchase gated. */
  ticketsEnabled: boolean;
  /** Freemium Discovery caps — null = unlimited (paid tiers). */
  discoveryVenueCap: number | null;
  discoveryLiveEventCap: number | null;
  themeUnlock: ThemeUnlockMode;
};

export const SEARCH_RADIUS_OPTIONS_KM = [10, 25, 50, 100] as const;
export type SearchRadiusKm = (typeof SEARCH_RADIUS_OPTIONS_KM)[number];

function entitlementsForPaid(
  delayHours: number,
  rest: Omit<
    ConsumerEntitlements,
    | "eventVisibilityDelayHours"
    | "earlyAccessHours"
    | "circleEnabled"
    | "ticketsEnabled"
    | "discoveryVenueCap"
    | "discoveryLiveEventCap"
    | "themeUnlock"
  >,
): ConsumerEntitlements {
  // Legacy earlyAccessHours = eliteAnchor(72) - delay, so old callers keep relative math.
  const eliteAnchor = EVENT_VISIBILITY_DELAY_HOURS.member; // 72 — historical elite earlyAccessHours
  return {
    ...rest,
    eventVisibilityDelayHours: delayHours,
    earlyAccessHours: eliteAnchor - delayHours,
    circleEnabled: true,
    ticketsEnabled: true,
    discoveryVenueCap: null,
    discoveryLiveEventCap: null,
    themeUnlock: "all",
  };
}

export const CONSUMER_ENTITLEMENTS_BY_TIER: Record<ConsumerTier, ConsumerEntitlements> = {
  free: {
    maxSearchRadius: 25,
    eventVisibilityHours: 48,
    maxInterestedMarkers: 10,
    maxSquads: 0,
    maxMemoriesPhotos: 0,
    recommendationCount: 0,
    eventVisibilityDelayHours: EVENT_VISIBILITY_DELAY_HOURS.free,
    // Legacy: 72 - 120 = -48 — do not use for gates; use eventVisibilityDelayHours.
    earlyAccessHours: EVENT_VISIBILITY_DELAY_HOURS.member - EVENT_VISIBILITY_DELAY_HOURS.free,
    notificationGranularity: "basic",
    prioritySupport: false,
    profileFlair: false,
    aiConciergeEnabled: false,
    circleEnabled: false,
    ticketsEnabled: false,
    discoveryVenueCap: 10,
    discoveryLiveEventCap: 3,
    themeUnlock: "platinum_only",
  },
  member: entitlementsForPaid(EVENT_VISIBILITY_DELAY_HOURS.member, {
    maxSearchRadius: 50,
    eventVisibilityHours: 48,
    maxInterestedMarkers: 50,
    maxSquads: 1,
    maxMemoriesPhotos: 5,
    recommendationCount: 3,
    notificationGranularity: "basic",
    prioritySupport: false,
    profileFlair: false,
    aiConciergeEnabled: false,
  }),
  circle_plus: entitlementsForPaid(EVENT_VISIBILITY_DELAY_HOURS.circle_plus, {
    maxSearchRadius: 100,
    eventVisibilityHours: 336,
    maxInterestedMarkers: null,
    maxSquads: null,
    maxMemoriesPhotos: 15,
    recommendationCount: 5,
    notificationGranularity: "basic",
    prioritySupport: false,
    profileFlair: false,
    aiConciergeEnabled: false,
  }),
  elite: entitlementsForPaid(EVENT_VISIBILITY_DELAY_HOURS.elite, {
    maxSearchRadius: null,
    eventVisibilityHours: null,
    maxInterestedMarkers: null,
    maxSquads: null,
    maxMemoriesPhotos: null,
    recommendationCount: 7,
    notificationGranularity: "extended",
    prioritySupport: true,
    profileFlair: true,
    aiConciergeEnabled: true,
  }),
};

export const PLATFORM_TRIAL_TIER: ConsumerTier = "elite";

export type ConsumerSubscriptionSnapshot = {
  tier: string;
  status: string;
  trial_end?: string | null;
  admin_access_extension_until?: string | null;
};

export type ResolvedConsumerAccess = {
  accessKind: AccessKind;
  tier: ConsumerTier | null;
  entitlements: ConsumerEntitlements | null;
};

export function isConsumerTier(value: string): value is ConsumerTier {
  return (CONSUMER_TIERS as readonly string[]).includes(value);
}

export function isPaidConsumerTier(value: string): value is PaidConsumerTier {
  return (PAID_CONSUMER_TIERS as readonly string[]).includes(value);
}

/** Rank in hierarchy: free < member < circle_plus < elite. Higher = more access. */
export function consumerTierRank(tier: ConsumerTier): number {
  return CONSUMER_TIERS.indexOf(tier);
}

/**
 * True when `tier` is at least `minimum` (inclusive) in the consumer hierarchy.
 * Elite includes all Circle+ rights; Circle+ includes all Member rights.
 */
export function consumerTierAtLeast(
  tier: ConsumerTier | null | undefined,
  minimum: ConsumerTier,
): boolean {
  if (!tier || !isConsumerTier(tier)) return false;
  return consumerTierRank(tier) >= consumerTierRank(minimum);
}

/** Live Radar unlocks at Circle+ and is included for Elite (not Free/Member). */
export function hasLiveRadarAccess(tier: ConsumerTier | null | undefined): boolean {
  return consumerTierAtLeast(tier, "circle_plus");
}

export function getEntitlementsForTier(tier: ConsumerTier): ConsumerEntitlements {
  return CONSUMER_ENTITLEMENTS_BY_TIER[tier];
}

/**
 * Normalize RPC entitlements so staffelung gates always see a finite
 * `eventVisibilityDelayHours` (Phase 1/2/3 field name).
 *
 * Option A: the SQL helper `consumer_entitlements_for_tier` is source of truth and
 * already returns this field on TE2. Older RPC payloads (pre-freemium migration)
 * only had legacy `earlyAccessHours` — without this merge, `passesEarlyAccessGate`
 * computes `createdMs + undefined * 3600000` → NaN and empties Discovery for every tier.
 *
 * Missing / non-finite delay is filled from `EVENT_VISIBILITY_DELAY_HOURS[tier]`,
 * not from remapping `earlyAccessHours` (that would re-anchor Option B).
 */
export function normalizeConsumerEntitlements(
  raw: Partial<ConsumerEntitlements> | null | undefined,
  tier: ConsumerTier | null | undefined,
): ConsumerEntitlements | null {
  if (!raw || !tier || !isConsumerTier(tier)) return null;
  const defaults = CONSUMER_ENTITLEMENTS_BY_TIER[tier];
  const delayHours =
    typeof raw.eventVisibilityDelayHours === "number" &&
    Number.isFinite(raw.eventVisibilityDelayHours)
      ? raw.eventVisibilityDelayHours
      : EVENT_VISIBILITY_DELAY_HOURS[tier];

  return {
    ...defaults,
    ...raw,
    eventVisibilityDelayHours: delayHours,
    // Keep legacy mirror coherent when RPC omitted freemium-era fields.
    earlyAccessHours:
      typeof raw.earlyAccessHours === "number" && Number.isFinite(raw.earlyAccessHours)
        ? raw.earlyAccessHours
        : defaults.earlyAccessHours,
    circleEnabled: raw.circleEnabled ?? defaults.circleEnabled,
    ticketsEnabled: raw.ticketsEnabled ?? defaults.ticketsEnabled,
    discoveryVenueCap:
      raw.discoveryVenueCap === undefined ? defaults.discoveryVenueCap : raw.discoveryVenueCap,
    discoveryLiveEventCap:
      raw.discoveryLiveEventCap === undefined
        ? defaults.discoveryLiveEventCap
        : raw.discoveryLiveEventCap,
    themeUnlock: raw.themeUnlock ?? defaults.themeUnlock,
  };
}

const ACTIVE_SUBSCRIPTION_STATUSES = new Set(["active", "trialing", "past_due"]);

function isTrialStillValid(trialEnd: string | null | undefined, nowMs = Date.now()): boolean {
  if (trialEnd == null || trialEnd === "") return true;
  const endMs = Date.parse(trialEnd);
  if (Number.isNaN(endMs)) return true;
  return endMs > nowMs;
}

function isAdminExtensionActive(
  until: string | null | undefined,
  nowMs = Date.now(),
): boolean {
  if (until == null || until === "") return false;
  const endMs = Date.parse(until);
  if (Number.isNaN(endMs)) return false;
  return endMs > nowMs;
}

export function resolveConsumerAccess(
  subscription: ConsumerSubscriptionSnapshot | null | undefined,
  nowMs = Date.now(),
): ResolvedConsumerAccess {
  if (!subscription) {
    return { accessKind: "expired", tier: null, entitlements: null };
  }

  // Freemium row: always "active" access with free entitlements (no Stripe status required).
  if (subscription.tier === "free" && isConsumerTier("free")) {
    return {
      accessKind: "active",
      tier: "free",
      entitlements: getEntitlementsForTier("free"),
    };
  }

  const statusActive = ACTIVE_SUBSCRIPTION_STATUSES.has(subscription.status);
  const extensionActive = isAdminExtensionActive(
    subscription.admin_access_extension_until,
    nowMs,
  );

  if (!statusActive && !extensionActive) {
    return { accessKind: "expired", tier: null, entitlements: null };
  }

  if (statusActive && subscription.status === "trialing") {
    if (!isTrialStillValid(subscription.trial_end, nowMs)) {
      if (extensionActive) {
        return {
          accessKind: "active",
          tier: PLATFORM_TRIAL_TIER,
          entitlements: getEntitlementsForTier(PLATFORM_TRIAL_TIER),
        };
      }
      return { accessKind: "expired", tier: null, entitlements: null };
    }
    return {
      accessKind: "trialing",
      tier: PLATFORM_TRIAL_TIER,
      entitlements: getEntitlementsForTier(PLATFORM_TRIAL_TIER),
    };
  }

  const tier = isConsumerTier(subscription.tier) ? subscription.tier : null;
  if (tier) {
    return {
      accessKind: "active",
      tier,
      entitlements: getEntitlementsForTier(tier),
    };
  }

  if (extensionActive) {
    return {
      accessKind: "active",
      tier: PLATFORM_TRIAL_TIER,
      entitlements: getEntitlementsForTier(PLATFORM_TRIAL_TIER),
    };
  }

  return { accessKind: "expired", tier: null, entitlements: null };
}

/** Lowest paid tier that unlocks a search radius option (for upsell copy). */
export function minTierForSearchRadiusKm(km: number): ConsumerTier {
  if (km <= CONSUMER_ENTITLEMENTS_BY_TIER.member.maxSearchRadius!) {
    return "member";
  }
  if (
    CONSUMER_ENTITLEMENTS_BY_TIER.circle_plus.maxSearchRadius != null &&
    km <= CONSUMER_ENTITLEMENTS_BY_TIER.circle_plus.maxSearchRadius
  ) {
    return "circle_plus";
  }
  return "elite";
}

export function isSearchRadiusAllowed(
  km: number,
  entitlements: ConsumerEntitlements,
): boolean {
  if (entitlements.maxSearchRadius == null) return true;
  return km <= entitlements.maxSearchRadius;
}

export function clampSearchRadiusKm(
  km: number,
  entitlements: ConsumerEntitlements,
): number {
  if (entitlements.maxSearchRadius == null) return km;
  return Math.min(km, entitlements.maxSearchRadius);
}

/** Lowest tier with unlimited interested markers (for upsell copy). */
export function minTierForUnlimitedInterestedMarkers(): ConsumerTier {
  return "circle_plus";
}

export function isInterestedMarkerAllowed(
  currentCount: number,
  entitlements: ConsumerEntitlements,
): boolean {
  if (entitlements.maxInterestedMarkers == null) return true;
  return currentCount < entitlements.maxInterestedMarkers;
}

export const INTERESTED_LIMIT_ERROR_CODE = "INTERESTED_LIMIT_REACHED";

export const SQUAD_LIMIT_ERROR_CODE = "SQUAD_LIMIT_REACHED";

/** Lowest tier with unlimited squads (for upsell copy). */
export function minTierForUnlimitedSquads(): ConsumerTier {
  return "circle_plus";
}

export function isSquadCountAllowed(
  currentCount: number,
  entitlements: ConsumerEntitlements,
): boolean {
  if (entitlements.maxSquads == null) return true;
  return currentCount < entitlements.maxSquads;
}

export const MEMORY_PHOTO_LIMIT_ERROR_CODE = "MEMORY_PHOTO_LIMIT_REACHED";

export function isMemoriesPhotoCountAllowed(
  currentCount: number,
  entitlements: ConsumerEntitlements,
): boolean {
  if (entitlements.maxMemoriesPhotos == null) return true;
  return currentCount < entitlements.maxMemoriesPhotos;
}

/** Tier that unlocks the next photo limit step (for upsell copy). */
export function memoriesPhotoUnlockTier(currentMax: number): ConsumerTier {
  if (currentMax <= CONSUMER_ENTITLEMENTS_BY_TIER.member.maxMemoriesPhotos!) {
    return "circle_plus";
  }
  return "elite";
}

export type EventVisibilityInput = {
  /** Prefer `created_at` (DB column). Alias accepted for client camelCase. */
  created_at?: string | null;
  createdAt?: string | null;
};

export type EventVisibilityResult = {
  visible: boolean;
  unlockAt: Date;
  reason: string;
};

function resolveCreatedAtIso(event: EventVisibilityInput): string | null | undefined {
  return event.created_at ?? event.createdAt;
}

/**
 * Staffelung gate: `unlockAt = created_at + EVENT_VISIBILITY_DELAY_HOURS[tier]`.
 *
 * Fail-CLOSED on missing/invalid `created_at` (behaviour change vs legacy
 * `passesEarlyAccessGate`, which treated missing as "visible now"):
 * only **elite** may see the event; all other tiers get `visible: false`.
 * Comment kept intentional — do not reintroduce fail-open without product sign-off.
 */
export function getEventVisibilityForTier(
  event: EventVisibilityInput,
  tier: ConsumerTier,
  now: Date = new Date(),
): EventVisibilityResult {
  const delayHours = EVENT_VISIBILITY_DELAY_HOURS[tier];
  const createdAtIso = resolveCreatedAtIso(event);
  const createdMs = createdAtIso ? Date.parse(createdAtIso) : Number.NaN;

  if (!createdAtIso || Number.isNaN(createdMs)) {
    if (tier === "elite") {
      return {
        visible: true,
        unlockAt: now,
        reason: "missing_created_at_elite_bypass",
      };
    }
    return {
      visible: false,
      unlockAt: now,
      reason: "missing_created_at_fail_closed",
    };
  }

  const unlockAt = new Date(createdMs + delayHours * 3_600_000);
  const visible = now.getTime() >= unlockAt.getTime();
  return {
    visible,
    unlockAt,
    reason: visible ? "unlocked" : "staffelung_pending",
  };
}

/**
 * When a listing becomes visible relative to created_at.
 *
 * Uses `entitlements.eventVisibilityDelayHours` (explicit offsets).
 * Fail-CLOSED on missing/invalid created_at (aligned with getEventVisibilityForTier;
 * previously fail-open). Callers without a known tier cannot use elite bypass —
 * missing created_at ⇒ not visible.
 */
export function passesEarlyAccessGate(
  createdAtIso: string | null | undefined,
  entitlements: ConsumerEntitlements,
  now: Date = new Date(),
): boolean {
  const createdMs = createdAtIso ? Date.parse(createdAtIso) : Number.NaN;
  if (!createdAtIso || Number.isNaN(createdMs)) {
    // Fail-closed (was fail-open). See getEventVisibilityForTier.
    return false;
  }
  const delayHours = entitlements.eventVisibilityDelayHours;
  // Non-finite delay (missing RPC field) must not NaN-gate the entire feed.
  // Callers should pass normalizeConsumerEntitlements(); this is defense in depth.
  if (typeof delayHours !== "number" || !Number.isFinite(delayHours)) {
    return false;
  }
  const visibleAtMs = createdMs + delayHours * 3_600_000;
  return now.getTime() >= visibleAtMs;
}

/**
 * Hard discovery filter: events beyond the tier visibility horizon are hidden.
 * Member: starts within 48h; Circle+: 2 weeks; Elite: unlimited horizon.
 * Free: same horizon as member (48h) after staffelung delay.
 */
export function isEventVisibleInDiscovery(
  createdAtIso: string | null | undefined,
  startsAtIso: string | null | undefined,
  entitlements: ConsumerEntitlements,
  now: Date = new Date(),
): boolean {
  if (!passesEarlyAccessGate(createdAtIso, entitlements, now)) return false;
  if (entitlements.eventVisibilityHours == null) return true;
  if (!startsAtIso) return true;
  const startsMs = Date.parse(startsAtIso);
  if (Number.isNaN(startsMs)) return true;
  const horizonMs = now.getTime() + entitlements.eventVisibilityHours * 3_600_000;
  return startsMs <= horizonMs;
}

export function isVenueVisibleInDiscovery(
  createdAtIso: string | null | undefined,
  entitlements: ConsumerEntitlements,
  now: Date = new Date(),
): boolean {
  return passesEarlyAccessGate(createdAtIso, entitlements, now);
}

export function isAiConciergeEnabled(entitlements: ConsumerEntitlements): boolean {
  return entitlements.aiConciergeEnabled;
}

/** Lowest tier that can see an event starting this many hours from now. */
export function minTierForEventHorizonHours(hoursUntilStart: number): ConsumerTier {
  const memberHours = CONSUMER_ENTITLEMENTS_BY_TIER.member.eventVisibilityHours!;
  const circleHours = CONSUMER_ENTITLEMENTS_BY_TIER.circle_plus.eventVisibilityHours!;
  if (hoursUntilStart <= memberHours) return "member";
  if (hoursUntilStart <= circleHours) return "circle_plus";
  return "elite";
}
